import React, { useMemo } from "react";
import {
  Collapse,
  Form,
  Row,
  Col,
  Select,
  Input,
  Space,
  Button,
  Checkbox,
  Spin,
  DatePicker,
} from "antd";
import type { RowProps } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import { useAppSelector } from "../..";
import type { DestinationListSearchProps } from "../../store/slices/destinations/types";
import {
  checkIsLoading,
  getSelectOptions,
  selectFilterOption,
  displayFormat,
} from "../../lib/utils";
import "./DestinationList.less";

interface ComponentProps {
  searchForm: FormInstance<DestinationListSearchProps>;
  onSearch: () => void;
  onResetSearch: () => void;
  searchAccountsByName: (name: string) => void;
  cleanSearchedAccounts: () => void;
  isLoading: boolean;
  setDestinationListFilterState: (props: Partial<DestinationListSearchProps>) => void;
}

/* ---------------------------------------------- Начало компонента ---------------------------------------------- */

export const DestinationsListSearch = (props: ComponentProps) => {
  const {
    searchForm,
    onSearch,
    onResetSearch,
    searchAccountsByName,
    cleanSearchedAccounts,
    isLoading,
    setDestinationListFilterState,
  } = props;

  const {
    destinationSets,
    searchedAccounts,
    searchedAccountsLoading,
    destinationListFilter,
  } = useAppSelector((state) => state.destinations);

  const formRowProps: RowProps = {
    gutter: 50,
    justify: "center",
  };

  const activeKey = useMemo(() => (destinationListFilter.filterOpen ? ["1"] : []), [
    destinationListFilter,
  ]);

  const onToggleCollapse = (keys: string | string[]) => {
    const filterOpen = keys.length > 0;
    setDestinationListFilterState({ filterOpen });
  };

  return (
    <Collapse
      expandIconPosition="right"
      className="destination-list-search"
      activeKey={activeKey}
      onChange={onToggleCollapse}
    >
      <Collapse.Panel header="Расширенный поиск" key="1">
        <Form
          style={{ marginTop: 20 }}
          form={searchForm}
          onFinish={onSearch}
          wrapperCol={{ span: 16 }}
          labelCol={{ span: 6 }}
          labelAlign="left"
          onValuesChange={setDestinationListFilterState}
        >
          <Row {...formRowProps}>
            <Col span={11}>
              <Form.Item
                dependencies={["accountId"]}
                wrapperCol={{ span: 24 }}
                className="form-item-required__hidden-error__mb0"
              >
                {({ getFieldValue }) => {
                  const accountId = getFieldValue("accountId");
                  return (
                    <Form.Item
                      label="Набор"
                      name="destinationSetId"
                      wrapperCol={{ span: 16 }}
                      labelCol={{ span: 6 }}
                      dependencies={["accountId"]}
                      rules={[
                        {
                          required: !accountId,
                        },
                      ]}
                    >
                      <Select
                        options={getSelectOptions({
                          list: destinationSets,
                        })}
                        showSearch
                        filterOption={selectFilterOption}
                        allowClear
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item label="Код" name="areaCode">
                <Input allowClear type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Row {...formRowProps}>
            <Col span={11}>
              <Form.Item
                label={
                  <span>
                    Наименование
                    <br />
                    направления
                  </span>
                }
                name="name"
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item
                dependencies={["destinationSetId"]}
                wrapperCol={{ span: 24 }}
                className="form-item-required__hidden-error__mb0"
              >
                {({ getFieldValue }) => {
                  const destinationSetId = getFieldValue("destinationSetId");
                  return (
                    <Form.Item
                      label={
                        <span>
                          Контрагент/
                          <br />
                          Оператор связи
                        </span>
                      }
                      name="accountId"
                      wrapperCol={{ span: 16 }}
                      labelCol={{ span: 6 }}
                      dependencies={["destinationSetId"]}
                      rules={[
                        {
                          required: !destinationSetId,
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        allowClear
                        notFoundContent={
                          checkIsLoading(searchedAccountsLoading) && <Spin size="small" />
                        }
                        filterOption={selectFilterOption}
                        onSearch={searchAccountsByName}
                        onClear={cleanSearchedAccounts}
                        options={getSelectOptions({
                          list: searchedAccounts.map((account) => ({
                            ...account,
                            name: account.organization.fullName,
                          })),
                        })}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
          <Row {...formRowProps}>
            <Col span={11}>
              <Form.Item label="Аббревиатура" name="shortName">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item
                label="Дата"
                name="onDate"
                className="form-item-required__hidden-error"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker format={displayFormat} />
              </Form.Item>
            </Col>
          </Row>
          <Row {...formRowProps}>
            <Col span={11}>
              <Form.Item label="Показать закрытые" name="showClosed" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Col>
            <Col span={11} />
          </Row>
          <Row justify="end">
            <Space>
              <Button htmlType="reset" onClick={onResetSearch}>
                Сброс параметров
              </Button>
              <Button type="primary" htmlType="submit" disabled={isLoading}>
                Применить
              </Button>
            </Space>
          </Row>
        </Form>
      </Collapse.Panel>
    </Collapse>
  );
};
